<template>
  <section
    ref="calculatorSectionRef"
    class="tuition-calculator-section"
    data-testid="tuition-calculator-section"
  >
    <div class="bg-light-1 parallax-block"></div>
    <div
      ref="calculatorContainerRef"
      class="container px-space-xs tuition-calculator-container"
      :class="{
        'tuition-calculator-container--has-start-over':
          activeView === 'results' || activeView === 'override'
      }"
    >
      <div
        ref="calculatorShellRef"
        class="tuition-calculator-shell row gx-0 py-lg-space-xl px-lg-space-xl py-space-md px-space-xs border bg-white"
        :class="{
          'tuition-calculator-shell--override': activeView === 'override'
        }"
      >
        <!-- <div class="col-12">
          <div
            class="d-flex justify-content-between align-items-center text-dark-2 mb-space-md"
          >
            <span
              class="fs-large fw-bold bg-secondary py-space-xxxs px-space-xxs"
              >Tuition and time estimator</span
            >
            <div class="d-flex align-items-center gap-space-sm">
              <span
                class="fs-small"
                v-if="activeView === 'details' || activeView === 'savings'"
                >Step {{ activeView === "details" ? "1" : "2" }} of 2</span
              >
              <button
                v-if="activeView === 'savings'"
                type="button"
                class="btn tuition-skip-pill fw-bold text-dark-3"
                data-testid="savings-skip"
                :disabled="requestStage === 'initial'"
                @click="skipSavings"
              >
                <span class="me-space-xxxs"> Skip this step </span>

                <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
              </button>
            </div>
          </div>
        </div> -->
        <div class="col-12" :class="{ 'mb-space-xs': activeView !== 'override' }">
          <Transition
            :name="transitionDirection === 'forward' ? 'calculator-forward' : 'calculator-backward'"
            mode="out-in"
            @after-enter="focusActiveHeading"
          >
            <section :key="activeView" ref="calculatorViewRef" class="calculator-view">
              <div v-if="activeView === 'details'" data-testid="details-page">
                <div
                  class="tuition-details-meta d-flex justify-content-between align-items-center text-dark-2 mb-space-md"
                >
                  <span
                    class="tuition-estimator-label fs-large fw-bold bg-secondary py-space-xxxs px-space-xxs text-dark-3"
                    >Tuition and time estimator</span
                  >
                  <div class="tuition-details-step d-flex align-items-center gap-space-sm">
                    <span class="fs-small">Step 1 of 2</span>
                  </div>
                </div>
                <p
                  class="fs-large text-dark-2 mb-space-md tuition-details-heading"
                  data-testid="details-heading"
                  tabindex="-1"
                >
                  Give your details
                </p>

                <DetailsFields
                  v-model="draftSelections"
                  :programs="props.programs"
                  :errors="validationErrors"
                  :credits-warning-message="props.pageData.creditsWarningMessage ?? ''"
                  :residency-status-title="props.pageData.residencyStatusTitle"
                  :residency-status-description="props.pageData.residencyStatusDescription"
                  @selection="handleSelection"
                  @credit-modal-open="trackCreditModalOpen"
                  @credit-modal-close-cross="trackCreditModalCloseCross"
                  @credit-modal-cancel="trackCreditModalCancel"
                  @credit-modal-confirm="trackCreditModalConfirm"
                  @credits-change="trackCreditsChange"
                  @faq-link="emit('faq-link', $event)"
                />
                <div class="tuition-details-actions d-flex justify-content-end">
                  <button
                    type="button"
                    class="btn btn-secondary py-space-xs px-space-md"
                    data-testid="details-next"
                    :disabled="!isDetailsValid"
                    @click="nextFromDetails"
                  >
                    <span class="me-space-xxxs"> Next </span>
                    <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
                  </button>
                </div>
              </div>

              <div v-else-if="activeView === 'savings'" data-testid="savings-page">
                <div
                  class="tuition-savings-step d-flex justify-content-end align-items-center text-dark-2 mb-space-md"
                >
                  <div class="d-flex align-items-center gap-space-sm">
                    <span class="fs-small">Step 2 of 2</span>
                  </div>
                </div>
                <div
                  class="tuition-savings-header border-bottom pb-space-lg mb-space-md d-flex justify-content-between align-items-end"
                >
                  <div class="tuition-savings-heading-col col-lg-6">
                    <h2
                      class="tuition-savings-heading h2-small text-dark-3 mb-0"
                      data-testid="savings-heading"
                      tabindex="-1"
                    >
                      {{ props.pageData.step2TitleText ?? "Add your details to see how you can" }}
                      <span
                        v-if="props.pageData.step2TitleHighlight"
                        class="tuition-highlight"
                        data-testid="savings-heading-highlight"
                        >{{ props.pageData.step2TitleHighlight }}</span
                      >
                    </h2>
                  </div>
                  <div class="tuition-savings-actions col-lg-6 d-flex justify-content-end">
                    <button
                      type="button"
                      class="btn tuition-skip-pill fw-bold text-dark-3"
                      data-testid="savings-skip"
                      :disabled="requestStage === 'initial'"
                      @click="skipSavings"
                    >
                      <span class="me-space-xxxs"> Skip this step </span>

                      <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
                    </button>
                  </div>
                </div>

                <SavingsFields
                  v-model="draftSelections"
                  :errors="validationErrors"
                  :transfer-warning-message="props.pageData.transferWarningMessage ?? ''"
                  :exception-program-codes="props.transferCreditExceptionProgramCodes"
                  :content="props.pageData"
                  @field-commit="trackSavingsFieldCommit"
                  @faq-link="emit('faq-link', $event)"
                />

                <Alert
                  v-if="requestError"
                  :show="true"
                  variant="warning"
                  data-testid="request-error"
                >
                  <p ref="requestAlertHeading" tabindex="-1" data-testid="request-error-message">
                    {{ requestError }}
                  </p>
                  <button
                    type="button"
                    class="btn btn-secondary"
                    data-testid="retry"
                    :disabled="requestStage === 'initial'"
                    @click="retryInitialEstimate"
                  >
                    Retry
                  </button>
                </Alert>

                <div class="d-flex justify-content-between align-items-center">
                  <button
                    type="button"
                    class="btn btn-link tuition-back-button fw-bold text-dark-3"
                    data-testid="savings-back"
                    @click="backToDetails"
                  >
                    <FontAwesomeIcon icon="fa-solid fa-arrow-left" />
                    <span class="ms-space-xxxs"> Back </span>
                  </button>
                  <button
                    type="button"
                    class="btn btn-secondary tuition-primary-button"
                    data-testid="view-breakdown"
                    :disabled="requestStage === 'initial'"
                    @click="viewBreakdown"
                  >
                    <span class="me-lg-space-xxxs">View cost breakdown</span>
                    <FontAwesomeIcon icon="fa-solid fa-arrow-right" />
                  </button>
                </div>
              </div>

              <div v-else-if="activeView === 'override'" data-testid="override-page">
                <OverrideView
                  v-if="draftSelections.program"
                  :page-data="props.pageData"
                  :program="draftSelections.program"
                  @program-link="handleOverrideProgramLink"
                  @start-over="startOver()"
                />
              </div>

              <ResultsView
                v-else
                :estimates="committedResultSnapshot.estimates"
                :semester-totals="committedResultSnapshot.semesterTotals"
                :active-academic-year="activeAcademicYear"
                :selections="committedSelections"
                :editing="editing"
                :pending="pending"
                :request-error="requestError"
                :page-data="props.pageData"
                @select-year="activeAcademicYear = $event"
                @include-summer="updateIncludeSummer"
                @edit="beginEdit"
                @cancel="cancelEdit"
                @recalculate="recalculate"
                @retry="retryRecalculation"
                @faq-link="emit('faq-link', $event)"
              >
                <template #edit-form>
                  <ResultEditForm
                    v-if="editDraft"
                    v-model="editDraft"
                    :programs="props.programs"
                    :errors="validationErrors"
                    :exception-program-codes="props.transferCreditExceptionProgramCodes"
                    :content="props.pageData"
                    @selection="handleEditSelection"
                    @credit-modal-open="trackCreditModalOpen"
                    @credit-modal-close-cross="trackCreditModalCloseCross"
                    @credit-modal-cancel="trackCreditModalCancel"
                    @credit-modal-confirm="trackCreditModalConfirm"
                    @credits-change="trackCreditsChange"
                    @field-commit="trackEditFieldCommit"
                    @faq-link="handleEditFaqLink"
                  />
                </template>
              </ResultsView>
            </section>
          </Transition>
        </div>
      </div>
      <div
        v-if="activeView === 'results'"
        class="tuition-results-footer d-flex justify-content-center"
      >
        <button
          type="button"
          class="btn btn-link tuition-start-over fw-bold fs-large"
          data-testid="start-over"
          @click="startOver(resultsSection)"
        >
          <FontAwesomeIcon icon="fa-solid fa-rotate-right" aria-hidden="true" />
          <span>Start over</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";

import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";

import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Alert from "~/components/AlertMessage.vue";
import DetailsFields from "~/components/what-it-costs-page/cost-calculator-v2/DetailsFields.vue";
import OverrideView from "~/components/what-it-costs-page/cost-calculator-v2/OverrideView.vue";
import ResultEditForm from "~/components/what-it-costs-page/cost-calculator-v2/ResultEditForm.vue";
import ResultsView from "~/components/what-it-costs-page/cost-calculator-v2/ResultsView.vue";
import SavingsFields from "~/components/what-it-costs-page/cost-calculator-v2/SavingsFields.vue";
import { useTuitionCostEstimate } from "~/composables/useTuitionCostEstimate";
import type { DegreeSeekingProgram, WhatItCosts } from "~/server/resolvers/resolvers-types";
import {
  TUITION_CREDIT_OPTION_LABELS,
  TUITION_DEGREE_TYPE_LABELS,
  TUITION_RESIDENCY_LABELS
} from "~/src/services/constants";
import {
  buildTuitionEstimateInput,
  cloneResultSnapshot,
  cloneSelections,
  createInitialSelections,
  normalizeSelectionsForCategory,
  validateDetails,
  validateSavings
} from "~/src/services/TuitionCalculatorFormService";
import type {
  FaqLinkTarget,
  TuitionCalculatorField,
  TuitionCalculatorResultSnapshot,
  TuitionCalculatorSelections,
  TuitionCalculatorValidationErrors
} from "~/types/tuition-calculator";
import type { TuitionEstimateErrorCode, TuitionEstimateInput } from "~/types/types";

type CalculatorView = "details" | "savings" | "results" | "override";
type TransitionDirection = "forward" | "backward";
type RequestStage = "initial" | "recalculate" | null;
type SavingsValidationField = Extract<
  TuitionCalculatorField,
  "transferCredits" | "grants" | "scholarships" | "employerTuitionBenefit"
>;

const analyticsRegion = "main content";
const analyticsSection = "asu online tuition calculator v2";
const analyticsComponent = "asu online tuition calculator v2";
const estimatorSection = "tuition and time estimator";
const resultsSection = "your estimated academic year total:";
const savingsSection = "add your details to see how you can reduce cost and time";
const creditModalSection = "enter your credits";

const selectionAnalytics: Record<string, { type: string; section: string }> = {
  degreeType: { type: "select a degree type", section: estimatorSection },
  program: { type: "select a program", section: estimatorSection },
  creditOption: { type: "select enrollment status", section: estimatorSection },
  creditsPerSemester: {
    type: creditModalSection,
    section: analyticsSection
  },
  residencyStatus: {
    type: "checkbox",
    section: "what is your arizona residency status?"
  }
};

const props = withDefaults(
  defineProps<{
    pageData: Partial<WhatItCosts>;
    programs: DegreeSeekingProgram[];
    transferCreditExceptionProgramCodes?: string[];
  }>(),
  {
    transferCreditExceptionProgramCodes: () => []
  }
);

const emit = defineEmits<{
  (event: "faq-link", target: FaqLinkTarget): void;
}>();

const draftSelections = ref<TuitionCalculatorSelections>(createInitialSelections());
const committedSelections = ref<TuitionCalculatorSelections>(createInitialSelections());
const editDraft = ref<TuitionCalculatorSelections | null>(null);
const editing = ref(false);

const activeView = ref<CalculatorView>("details");
const transitionDirection = ref<TransitionDirection>("forward");
const activeAcademicYear = ref<number>(0);
const requestStage = ref<RequestStage>(null);
const requestError = ref<string | null>(null);
const validationErrors = ref<TuitionCalculatorValidationErrors>({});
const requestAlertHeading = ref<HTMLElement | null>(null);
const calculatorSectionRef = ref<HTMLElement | null>(null);
const calculatorContainerRef = ref<HTMLElement | null>(null);
const calculatorShellRef = ref<HTMLElement | null>(null);
const calculatorViewRef = ref<HTMLElement | null>(null);
const scrollCalculatorTopPending = ref(false);
let scrollRequestId = 0;
let scrollAnimationFrameIds: number[] = [];
let scrollTimeoutIds: number[] = [];
let savingsValidationTimeout: ReturnType<typeof setTimeout> | null = null;

const savingsValidationFields: readonly SavingsValidationField[] = [
  "transferCredits",
  "grants",
  "scholarships",
  "employerTuitionBenefit"
];

const clearSavingsValidationTimeout = (): void => {
  if (!savingsValidationTimeout) return;
  clearTimeout(savingsValidationTimeout);
  savingsValidationTimeout = null;
};

const clearScheduledScroll = (): void => {
  scrollRequestId += 1;

  if (typeof window !== "undefined") {
    scrollAnimationFrameIds.forEach((frameId) => window.cancelAnimationFrame(frameId));
    scrollTimeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
  }

  scrollAnimationFrameIds = [];
  scrollTimeoutIds = [];
};

const scheduleScrollFrame = (callback: FrameRequestCallback): void => {
  if (typeof window === "undefined") return;
  const frameId = window.requestAnimationFrame(callback);
  scrollAnimationFrameIds.push(frameId);
};

const scheduleScrollTimeout = (callback: () => void, delay: number): void => {
  if (typeof window === "undefined") return;
  const timeoutId = window.setTimeout(callback, delay);
  scrollTimeoutIds.push(timeoutId);
};

const validateSavingsAfterInputDelay = (
  selections: TuitionCalculatorSelections,
  changedField: SavingsValidationField | null
): void => {
  clearSavingsValidationTimeout();

  if (activeView.value !== "savings") return;

  savingsValidationTimeout = setTimeout(() => {
    if (activeView.value !== "savings") {
      savingsValidationTimeout = null;
      return;
    }

    validationErrors.value = savingsErrorsForChangedField(selections, changedField);
    savingsValidationTimeout = null;
  }, 600);
};

const getChangedSavingsField = (
  selections: TuitionCalculatorSelections | null | undefined,
  previousSelections: TuitionCalculatorSelections | null | undefined
): SavingsValidationField | null => {
  if (!selections || !previousSelections) return null;
  return (
    savingsValidationFields.find((field) => selections[field] !== previousSelections[field]) ?? null
  );
};

const savingsErrorsForChangedField = (
  selections: TuitionCalculatorSelections,
  changedField: SavingsValidationField | null
): TuitionCalculatorValidationErrors => {
  const errors = validateSavings(selections, props.transferCreditExceptionProgramCodes).errors;

  if (changedField !== "transferCredits" && !validationErrors.value.transferCredits) {
    delete errors.transferCredits;
  }

  return errors;
};

watch(
  draftSelections,
  (selections, previousSelections) => {
    validateSavingsAfterInputDelay(
      selections,
      getChangedSavingsField(selections, previousSelections)
    );
  },
  { deep: true }
);

onBeforeUnmount(() => {
  clearSavingsValidationTimeout();
  clearScheduledScroll();
});

watch(
  editDraft,
  (selections, previousSelections) => {
    if (!editing.value || !selections) return;
    const detailErrors = validateDetails(selections);
    const detailErrorsVisible = Object.keys(detailErrors).some(
      (field) => field in validationErrors.value
    );
    validationErrors.value = {
      ...(detailErrorsVisible ? detailErrors : {}),
      ...savingsErrorsForChangedField(
        selections,
        getChangedSavingsField(selections, previousSelections)
      )
    };
  },
  { deep: true }
);

const requestInput = ref<TuitionEstimateInput>({
  costAcademicYear: 0,
  latestAcademicYear: 0,
  program_code: "",
  residency_status: "",
  degree_type: "",
  fee_code: "",
  total_credits: 0,
  transfer_credits: 0,
  grants: 0,
  scholarships: 0,
  employer_tuition_benefit: 0,
  credits_per_semester: 1,
  include_summer: true
});
const committedResultSnapshot = ref<TuitionCalculatorResultSnapshot>({
  estimates: [],
  semesterTotals: {}
});
const { estimates, semesterTotals, pending, error, execute, clearError } =
  useTuitionCostEstimate(requestInput);

const isDetailsValid = computed(
  () => Object.keys(validateDetails(draftSelections.value)).length === 0
);

const detailsFieldIds: Record<string, string> = {
  degreeType: "tuition-calculator-degree-type_typeahead__search",
  program: "tuition-calculator-program_typeahead__search",
  creditsPerSemester: "tuition-calculator-credit-option_typeahead__search",
  residencyStatus: "tuition-calculator-residency"
};

const savingsFieldIds: Record<string, string> = {
  transferCredits: "tuition-calculator-transfer-credits",
  grants: "tuition-calculator-grants",
  scholarships: "tuition-calculator-scholarships",
  employerTuitionBenefit: "tuition-calculator-employer-benefit"
};

// ResultEditForm renders its own fields with its own element ids (distinct
// from DetailsFields/SavingsFields on Page 1/2), so the edit-slot focus map
// mirrors detailsFieldIds/savingsFieldIds shape but points at the actual
// TypeAheadSelect search input ids.
const editFieldIds = computed<Record<string, string>>(() => ({
  degreeType: "tuition-edit-degree-type_typeahead__search",
  program: "tuition-edit-program_typeahead__search",
  creditsPerSemester: "tuition-edit-credit-option_typeahead__search",
  residencyStatus: "tuition-edit-residency_typeahead__search",
  transferCredits: "tuition-edit-transfer-credits",
  grants: "tuition-edit-grants",
  scholarships: "tuition-edit-scholarships",
  employerTuitionBenefit: "tuition-edit-employer-benefit"
}));

const focusFirstInvalidField = (
  errors: TuitionCalculatorValidationErrors,
  ids: Record<string, string>
): void => {
  const [field] = Object.keys(errors);
  const id = field ? ids[field] : undefined;
  if (id) document.getElementById(id)?.focus();
};

const scrollElementToTop = (target: HTMLElement | null): void => {
  if (!target || typeof window === "undefined") return;

  const top =
    target.getBoundingClientRect().top +
    window.scrollY -
    Number.parseFloat(getComputedStyle(target).scrollMarginTop || "0");

  const nextTop = Math.max(0, top);

  window.scrollTo({
    top: nextTop,
    behavior: "auto"
  });
  document.documentElement.scrollTop = nextTop;
  document.body.scrollTop = nextTop;
};

const scheduleScroll = (getTarget: () => HTMLElement | null): void => {
  if (typeof window === "undefined") return;

  clearScheduledScroll();
  const requestId = scrollRequestId;
  const scrollIfCurrent = (): void => {
    if (requestId !== scrollRequestId) return;
    scrollElementToTop(getTarget());
  };

  scrollIfCurrent();
  scheduleScrollFrame(() => {
    scrollIfCurrent();
    scheduleScrollFrame(scrollIfCurrent);
  });
  scheduleScrollTimeout(scrollIfCurrent, 80);
  scheduleScrollTimeout(scrollIfCurrent, 180);
  scheduleScrollTimeout(scrollIfCurrent, 360);
  scheduleScrollTimeout(scrollIfCurrent, 600);
};

const getCalculatorScrollTarget = (): HTMLElement | null => {
  if (activeView.value === "override") {
    return (
      calculatorViewRef.value ??
      calculatorSectionRef.value ??
      calculatorContainerRef.value ??
      calculatorShellRef.value
    );
  }

  return calculatorShellRef.value ?? calculatorContainerRef.value ?? calculatorSectionRef.value;
};

const scheduleCalculatorSectionScroll = (): void => {
  scheduleScroll(() => getCalculatorScrollTarget());
};

const scrollToRecalculatedSectionTop = async (): Promise<void> => {
  await nextTick();
  scheduleCalculatorSectionScroll();
  scheduleScrollTimeout(scheduleCalculatorSectionScroll, 700);
};

const focusActiveHeading = (el: Element): void => {
  const heading = (el as HTMLElement).querySelector<HTMLElement>("[tabindex='-1']");
  heading?.focus({ preventScroll: true });

  if (!scrollCalculatorTopPending.value) return;
  scrollCalculatorTopPending.value = false;

  scheduleCalculatorSectionScroll();
};

const showView = (view: CalculatorView, direction: TransitionDirection): void => {
  scrollCalculatorTopPending.value = true;
  transitionDirection.value = direction;
  activeView.value = view;
  scheduleCalculatorSectionScroll();
  void nextTick(() => scheduleCalculatorSectionScroll());
};

const editSelectionAnalytics: Record<string, { type: string; section: string }> = {
  degreeType: { type: "select a degree type", section: resultsSection },
  program: { type: "select a program", section: resultsSection },
  creditOption: {
    type: "select enrollment status",
    section: resultsSection
  },
  residencyStatus: { type: "select residency", section: resultsSection }
};

const editFieldSections: Record<string, string> = {
  transferCredits: `${resultsSection} ^ transfer credits`,
  grants: `${resultsSection} ^ grants`,
  scholarships: `${resultsSection} ^ scholarships`,
  employerTuitionBenefit: `${resultsSection} ^ employer tuition benefit`
};

const trackEditFieldCommit = (field: string, value: number): void => {
  analyticsComposable.trackFormEvent(
    "onclick",
    "click",
    "click",
    analyticsRegion,
    editFieldSections[field] ?? resultsSection,
    String(value),
    analyticsComponent
  );
};

const trackSelection = (
  field: string,
  text: string,
  analytics: Record<string, { type: string; section: string }>
): void => {
  const safeText = field === "creditsPerSemester" ? "custom amount" : text;
  const { type, section } = analytics[field] ??
    selectionAnalytics[field] ?? {
      type: field,
      section: analyticsSection
    };
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    type,
    analyticsRegion,
    section,
    safeText.toLowerCase(),
    analyticsComponent
  );
};

const handleSelection = (field: string, text: string): void =>
  trackSelection(field, text, selectionAnalytics);

const handleEditSelection = (field: string, text: string): void =>
  trackSelection(field, text, editSelectionAnalytics);

const trackCreditModalOpen = (): void => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "open",
    "click",
    analyticsRegion,
    "select enrollment status",
    "enter credits",
    analyticsComponent
  );
};

const trackCreditModalCloseCross = (): void => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "close",
    "click",
    analyticsRegion,
    creditModalSection,
    "close cross",
    analyticsComponent
  );
};

const selectionParameters = (
  selections: TuitionCalculatorSelections,
  options: { includeCredits?: boolean } = {}
): Record<string, string> => ({
  career: selections.degreeType ? TUITION_DEGREE_TYPE_LABELS[selections.degreeType] : "",
  program: selections.program?.title?.toLowerCase() ?? "",
  enrollment_status: selections.creditOption
    ? TUITION_CREDIT_OPTION_LABELS[selections.creditOption].toLowerCase()
    : "",
  ...(options.includeCredits
    ? { credits_per_semester: String(selections.creditsPerSemester ?? 0) }
    : {}),
  residency: selections.residencyStatus
    ? TUITION_RESIDENCY_LABELS[selections.residencyStatus].toLowerCase()
    : "",
  transfer_credits: String(selections.transferCredits ?? 0),
  grants: String(selections.grants ?? 0),
  scholarships: String(selections.scholarships ?? 0),
  employer_tuition_benefit: String(selections.employerTuitionBenefit ?? 0)
});

const trackSavingsAction = (text: string): void => {
  analyticsComposable.trackEvent({
    event: "form",
    name: "onclick",
    action: "click",
    type: "click",
    region: analyticsRegion,
    section: savingsSection,
    text,
    component: analyticsComponent,
    ...selectionParameters(draftSelections.value, { includeCredits: true })
  });
};

const savingsFieldSections: Record<string, string> = {
  transferCredits: "transfer credits",
  grants: "grants",
  scholarships: "scholarships",
  employerTuitionBenefit: "employer tuition benefit"
};

const trackSavingsFieldCommit = (field: string, value: number): void => {
  analyticsComposable.trackFormEvent(
    "onclick",
    "click",
    "click",
    analyticsRegion,
    savingsFieldSections[field] ?? field,
    String(value),
    analyticsComponent
  );
};

const trackCreditModalCancel = (): void => {
  analyticsComposable.trackModalEvent(
    "onclick",
    "close",
    "click",
    analyticsRegion,
    creditModalSection,
    "cancel",
    analyticsComponent
  );
};

const trackCreditModalConfirm = (credits: number): void => {
  analyticsComposable.trackEvent({
    event: "form",
    name: "onclick",
    action: "click",
    type: "click",
    region: analyticsRegion,
    section: creditModalSection,
    text: "save credit load",
    component: analyticsComponent,
    credits_per_semester: String(credits)
  });
};

const trackCreditsChange = (credits: number): void => {
  analyticsComposable.trackFormEvent(
    "onclick",
    "click",
    "click",
    analyticsRegion,
    creditModalSection,
    String(credits),
    analyticsComponent
  );
};

const trackAction = (text: string, section = analyticsSection): void => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    analyticsRegion,
    section,
    text,
    analyticsComponent
  );
};

const trackRequestError = (
  stage: "initial" | "recalculate",
  code: TuitionEstimateErrorCode
): void => {
  analyticsComposable.trackEvent({
    event: "error",
    name: "tuition_calculator_request_error",
    action: "display",
    type: code,
    region: analyticsRegion,
    section: stage,
    text: "tuition estimate unavailable",
    component: analyticsComponent
  });
};

const nextFromDetails = async (): Promise<void> => {
  const errors = validateDetails(draftSelections.value);
  validationErrors.value = errors;

  if (Object.keys(errors).length > 0) {
    await nextTick();
    focusFirstInvalidField(errors, detailsFieldIds);
    return;
  }

  analyticsComposable.trackFormEvent(
    "onclick",
    "click",
    "click",
    analyticsRegion,
    estimatorSection,
    "next",
    analyticsComponent
  );
  validationErrors.value = {};
  requestError.value = null;
  clearError();
  showView(draftSelections.value.program?.costOverride ? "override" : "savings", "forward");
};

const backToDetails = (): void => {
  trackAction("back", savingsSection);
  showView("details", "backward");
};

const requestInitialEstimate = async (): Promise<void> => {
  requestStage.value = "initial";
  requestError.value = null;
  clearError();

  try {
    requestInput.value = buildTuitionEstimateInput(draftSelections.value);
  } catch {
    requestStage.value = null;
    requestError.value = "We could not calculate your estimate. Please try again.";
    trackRequestError("initial", "validation");
    await nextTick();
    requestAlertHeading.value?.focus();
    return;
  }

  await execute();

  if (error.value || estimates.value.length === 0) {
    requestStage.value = null;
    requestError.value = "We could not calculate your estimate. Please try again.";
    trackRequestError("initial", error.value?.code ?? "response");
    await nextTick();
    requestAlertHeading.value?.focus();
    return;
  }

  committedSelections.value = cloneSelections(draftSelections.value);
  committedResultSnapshot.value = cloneResultSnapshot({
    estimates: estimates.value,
    semesterTotals: semesterTotals.value
  });
  activeAcademicYear.value = Number(committedSelections.value.program?.costAcademicYear);
  showView("results", "forward");
  requestStage.value = null;
};

const validateSavingsForSubmit = async (): Promise<boolean> => {
  const { errors } = validateSavings(
    draftSelections.value,
    props.transferCreditExceptionProgramCodes
  );
  validationErrors.value = errors;

  if (Object.keys(errors).length === 0) return true;

  await nextTick();
  focusFirstInvalidField(errors, savingsFieldIds);
  return false;
};

const skipSavings = async (): Promise<void> => {
  draftSelections.value = {
    ...normalizeSelectionsForCategory(draftSelections.value),
    transferCredits: 0,
    grants: 0,
    scholarships: 0,
    employerTuitionBenefit: 0
  };

  if (!(await validateSavingsForSubmit())) return;

  trackSavingsAction("skip this step");
  await requestInitialEstimate();
};

const viewBreakdown = async (): Promise<void> => {
  if (!(await validateSavingsForSubmit())) return;

  trackSavingsAction("view cost breakdown");
  draftSelections.value = normalizeSelectionsForCategory(draftSelections.value);
  await requestInitialEstimate();
};

const retryInitialEstimate = (): Promise<void> => {
  trackAction("retry");
  return requestInitialEstimate();
};

const beginEdit = (): void => {
  trackAction("edit your selections", resultsSection);
  editDraft.value = cloneSelections(committedSelections.value);
  validationErrors.value = {};
  requestError.value = null;
  editing.value = true;
};

const cancelEdit = (): void => {
  trackAction("cancel", resultsSection);
  requestInput.value = buildTuitionEstimateInput(committedSelections.value);
  clearError();
  requestError.value = null;
  validationErrors.value = {};
  editDraft.value = cloneSelections(committedSelections.value);
  editing.value = false;
  requestStage.value = null;
};

const closeEditWithoutSaving = (): void => {
  requestInput.value = buildTuitionEstimateInput(committedSelections.value);
  clearError();
  requestError.value = null;
  validationErrors.value = {};
  editDraft.value = cloneSelections(committedSelections.value);
  editing.value = false;
  requestStage.value = null;
};

const isMobileEditViewport = (): boolean =>
  typeof window !== "undefined" && window.innerWidth < 992;

const handleEditFaqLink = async (target: FaqLinkTarget): Promise<void> => {
  if (isMobileEditViewport()) {
    closeEditWithoutSaving();
    await nextTick();
  }

  emit("faq-link", target);
};

const recalculate = async (): Promise<void> => {
  if (!editDraft.value) return;

  const errors = {
    ...validateDetails(editDraft.value),
    ...validateSavings(editDraft.value, props.transferCreditExceptionProgramCodes).errors
  };

  if (Object.keys(errors).length > 0) {
    validationErrors.value = errors;
    await nextTick();
    focusFirstInvalidField(errors, editFieldIds.value);
    return;
  }

  analyticsComposable.trackEvent({
    event: "form",
    name: "onclick",
    action: "click",
    type: "click",
    region: analyticsRegion,
    section: resultsSection,
    text: "recalculate",
    component: analyticsComponent,
    ...selectionParameters(editDraft.value, { includeCredits: true })
  });
  validationErrors.value = {};
  const normalizedDraft = normalizeSelectionsForCategory(editDraft.value);

  if (normalizedDraft.program?.costOverride) {
    draftSelections.value = normalizedDraft;
    editDraft.value = cloneSelections(normalizedDraft);
    editing.value = false;
    requestError.value = null;
    showView("override", "forward");
    return;
  }

  requestStage.value = "recalculate";
  requestError.value = null;
  clearError();

  try {
    requestInput.value = buildTuitionEstimateInput(normalizedDraft);
  } catch {
    requestStage.value = null;
    requestError.value = "We could not update your estimate. Please try again.";
    trackRequestError("recalculate", "validation");
    return;
  }

  await execute();

  if (error.value || estimates.value.length === 0) {
    requestStage.value = null;
    requestError.value = "We could not update your estimate. Please try again.";
    trackRequestError("recalculate", error.value?.code ?? "response");
    return;
  }

  committedSelections.value = cloneSelections(normalizedDraft);
  committedResultSnapshot.value = cloneResultSnapshot({
    estimates: estimates.value,
    semesterTotals: semesterTotals.value
  });
  editing.value = false;
  requestStage.value = null;
  requestError.value = null;
  activeAcademicYear.value = Number(committedSelections.value.program?.costAcademicYear);
  await scrollToRecalculatedSectionTop();
};

const retryRecalculation = (): Promise<void> => {
  trackAction("retry");
  return recalculate();
};

const updateIncludeSummer = async (value: boolean): Promise<void> => {
  committedSelections.value = {
    ...committedSelections.value,
    includeSummer: value
  };
  if (editDraft.value) {
    editDraft.value = { ...editDraft.value, includeSummer: value };
  }
  requestInput.value = { ...requestInput.value, include_summer: value };
  await nextTick();

  if (error.value) return;

  committedResultSnapshot.value = cloneResultSnapshot({
    estimates: estimates.value,
    semesterTotals: semesterTotals.value
  });
};

const startOver = (section: string = analyticsSection): void => {
  trackAction("start over", section);
  clearError();
  draftSelections.value = createInitialSelections();
  committedSelections.value = createInitialSelections();
  editDraft.value = null;
  editing.value = false;
  committedResultSnapshot.value = { estimates: [], semesterTotals: {} };
  validationErrors.value = {};
  requestError.value = null;
  requestStage.value = null;
  activeAcademicYear.value = 0;
  showView("details", "backward");
};

const handleOverrideProgramLink = (programTitle: string): void => {
  trackAction(programTitle.toLowerCase());
};

defineExpose({ activeView, transitionDirection });
</script>

<style scoped lang="scss">
.tuition-calculator-section {
  overflow-anchor: none;
  scroll-margin-top: 16px;
}

.parallax-block {
  height: 256px;
  margin-bottom: -256px;
}

.tuition-details-heading {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--rds-border-color, #d8d8d8);
}

.tuition-highlight {
  background: #78be20;
  padding: 0 0.25rem;
  box-decoration-break: clone;
}
.tuition-skip-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  gap: 0.25rem;
  white-space: nowrap;
}

.tuition-skip-pill:hover,
.tuition-skip-pill:focus {
  background: var(--rds-light-2, #e8e8e8);
  color: var(--rds-dark-3, #191919);
}

.tuition-skip-pill:focus-visible {
  outline: 2px solid var(--rds-primary, #8c1d40);
  outline-offset: 2px;
}

.tuition-skip-pill:disabled {
  opacity: 0.65;
}

.tuition-primary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  white-space: nowrap;
}

.tuition-primary-button svg {
  flex: 0 0 auto;
}

.tuition-calculator-container {
  margin-bottom: 64px;
  overflow-anchor: none;
}

.tuition-calculator-container--has-start-over {
  margin-bottom: 32px;
}

.tuition-calculator-shell {
  scroll-margin-top: 2rem;
}

.tuition-results-footer {
  margin-top: 32px;
}

.calculator-view {
  scroll-margin-top: 5rem;
}

.tuition-start-over {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  color: var(--rds-primary, #8c1d40);
  text-decoration: underline;
}

.tuition-start-over:hover,
.tuition-start-over:focus {
  color: var(--rds-primary, #8c1d40);
}

.tuition-calculator-shell--override {
  padding-top: 0 !important;
  padding-right: 0 !important;
  padding-bottom: 0 !important;
  padding-left: 0 !important;
  border: 0 !important;
  background: transparent !important;
}

@media (min-width: 992px) {
  .tuition-calculator-container {
    max-width: 960px;
  }

  .calculator-view {
    scroll-margin-top: 7rem;
  }

  .tuition-calculator-shell {
    scroll-margin-top: 3rem;
  }
}

@media (max-width: 991.98px) {
  .tuition-calculator-container {
    margin-bottom: 48px;
  }

  .tuition-calculator-container--has-start-over {
    margin-bottom: 32px;
  }

  .tuition-primary-button {
    padding-right: 0.875rem;
    padding-left: 0.875rem;
    font-size: 0.875rem;
  }

  .tuition-calculator-shell {
    scroll-margin-top: 1.5rem;
  }

  .tuition-details-meta {
    flex-direction: column;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    gap: 1rem;
  }

  .tuition-details-step {
    order: 1;
  }

  .tuition-estimator-label {
    order: 2;
  }

  .tuition-details-actions {
    margin-top: 1.5rem;
  }

  .tuition-details-actions .btn {
    width: 100%;
    padding-top: 0.875rem;
    padding-bottom: 0.875rem;
  }

  .tuition-savings-step {
    justify-content: flex-start !important;
    margin-bottom: 2.5rem !important;
  }

  .tuition-savings-header {
    flex-direction: column;
    align-items: stretch !important;
    gap: 2rem;
    padding-bottom: 3.75rem !important;
  }

  .tuition-savings-heading-col,
  .tuition-savings-actions {
    width: 100%;
  }

  .tuition-savings-heading {
    max-width: 21rem;
    font-size: 36px;
    line-height: 1.08;
  }

  .tuition-savings-heading .tuition-highlight {
    display: inline;
    padding: 0 0.125rem;
  }

  .tuition-savings-actions {
    justify-content: center !important;
  }

  .tuition-savings-actions .tuition-skip-pill {
    width: 100%;
    border: 1px solid #191919;
    padding-top: 0.875rem;
    padding-bottom: 0.875rem;
  }

  .tuition-savings-actions .tuition-skip-pill:active {
    background: #e8e8e8;
  }
}

.calculator-forward-enter-active,
.calculator-forward-leave-active,
.calculator-backward-enter-active,
.calculator-backward-leave-active {
  transition:
    transform 0.24s ease,
    opacity 0.24s ease;
}

.calculator-forward-enter-from {
  transform: translateX(24px);
  opacity: 0;
}
.calculator-forward-leave-to {
  transform: translateX(-24px);
  opacity: 0;
}

.calculator-backward-enter-from {
  transform: translateX(-24px);
  opacity: 0;
}
.calculator-backward-leave-to {
  transform: translateX(24px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .calculator-forward-enter-active,
  .calculator-forward-leave-active,
  .calculator-backward-enter-active,
  .calculator-backward-leave-active {
    transition-duration: 0s;
  }

  .calculator-forward-enter-from,
  .calculator-forward-leave-to,
  .calculator-backward-enter-from,
  .calculator-backward-leave-to {
    transform: none;
  }
}
</style>
